"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { WsDropUpdateMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * Interface for tracking new drops count for a wave
 */
export interface MinimalWaveNewDropsCount {
  readonly count: number;
  readonly latestDropTimestamp: number | null;
  readonly firstUnreadSerialNo: number | null;
}
interface UseNewDropCounterOptions {
  readonly otherListWaveIds?: ReadonlySet<string> | undefined;
  readonly unknownWaveRefetchCooldownMs?: number | undefined;
}
const DEFAULT_UNKNOWN_WAVE_REFETCH_COOLDOWN_MS = 3000;
const DEFAULT_OTHER_LIST_WAVE_IDS: ReadonlySet<string> = new Set<string>();
export function getNewestTimestamp(cached: number | null | undefined = null, server: number | null | undefined = null): number | null {
  if (cached == null && server == null) {
    return null;
  }
  if (cached == null) {
    return server;
  }
  if (server == null) {
    return cached;
  }
  return Math.max(cached, server);
}

/**
 * Hook to manage new drop counts via WebSockets
 *
 * @param activeWaveId - The ID of the currently active wave
 * @param waves - List of waves for which to track new drops
 * @param refetchWaves - Function to refetch waves data when needed
 * @returns Object containing newDropsCounts and reset function
 */
function useNewDropCounter(activeWaveId, waves, refetchWaves, t0) {
  const $ = _c(26);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    connectedProfile
  } = useContext(AuthContext);
  const {
    otherListWaveIds: t2,
    unknownWaveRefetchCooldownMs: t3
  } = options;
  const otherListWaveIds = t2 === undefined ? DEFAULT_OTHER_LIST_WAVE_IDS : t2;
  const unknownWaveRefetchCooldownMs = t3 === undefined ? DEFAULT_UNKNOWN_WAVE_REFETCH_COOLDOWN_MS : t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [newDropsCounts, setNewDropsCounts] = useState(t4);
  const lastUnknownWaveRefetchAtRef = useRef(null);
  let t5;
  if ($[3] !== waves) {
    t5 = waveId => {
      setNewDropsCounts(prev => ({
        ...prev,
        [waveId]: {
          count: 0,
          latestDropTimestamp: getNewestTimestamp(prev[waveId]?.latestDropTimestamp, waves.find(wave => wave.id === waveId)?.metrics.latest_drop_timestamp ?? null),
          firstUnreadSerialNo: null
        }
      }));
    };
    $[3] = waves;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const resetWaveNewDropsCount = t5;
  let t6;
  if ($[5] !== waves) {
    t6 = () => {
      setNewDropsCounts(prev_0 => {
        const newCounts = {};
        waves.forEach(wave_0 => {
          newCounts[wave_0.id] = {
            count: 0,
            latestDropTimestamp: getNewestTimestamp(prev_0[wave_0.id]?.latestDropTimestamp, wave_0.metrics.latest_drop_timestamp ?? null),
            firstUnreadSerialNo: null
          };
        });
        return newCounts;
      });
    };
    $[5] = waves;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const resetAllWavesNewDropsCount = t6;
  let t7;
  let t8;
  if ($[7] !== activeWaveId || $[8] !== resetWaveNewDropsCount) {
    t7 = () => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible" && activeWaveId) {
          resetWaveNewDropsCount(activeWaveId);
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };
    t8 = [activeWaveId, resetWaveNewDropsCount];
    $[7] = activeWaveId;
    $[8] = resetWaveNewDropsCount;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[11] !== activeWaveId || $[12] !== resetWaveNewDropsCount) {
    t9 = () => {
      if (activeWaveId) {
        resetWaveNewDropsCount(activeWaveId);
      }
    };
    t10 = [activeWaveId, resetWaveNewDropsCount];
    $[11] = activeWaveId;
    $[12] = resetWaveNewDropsCount;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] !== activeWaveId || $[16] !== connectedProfile?.handle || $[17] !== otherListWaveIds || $[18] !== refetchWaves || $[19] !== unknownWaveRefetchCooldownMs || $[20] !== waves) {
    t11 = message => {
      if (!message?.wave.id) {
        return;
      }
      const waveId_0 = message.wave.id;
      const wave_1 = waves.find(w => w.id === waveId_0);
      if (!wave_1) {
        if (otherListWaveIds.has(waveId_0)) {
          return;
        }
        const now = Date.now();
        if (lastUnknownWaveRefetchAtRef.current !== null && now - lastUnknownWaveRefetchAtRef.current < unknownWaveRefetchCooldownMs) {
          return;
        }
        lastUnknownWaveRefetchAtRef.current = now;
        refetchWaves();
        return;
      }
      if (wave_1.metrics.muted) {
        return;
      }
      if (connectedProfile?.handle?.toLowerCase() === message.author.handle?.toLowerCase()) {
        return setNewDropsCounts(prev_1 => {
          const currentCount = prev_1[waveId_0]?.count ?? 0;
          const currentLatestDropTimestamp = prev_1[waveId_0]?.latestDropTimestamp ?? null;
          return {
            ...prev_1,
            [waveId_0]: {
              count: currentCount,
              latestDropTimestamp: Math.max(message.created_at, currentLatestDropTimestamp ?? 0),
              firstUnreadSerialNo: prev_1[waveId_0]?.firstUnreadSerialNo ?? null
            }
          };
        });
      }
      if (waveId_0 === activeWaveId && document.visibilityState === "visible") {
        return setNewDropsCounts(prev_2 => {
          const currentCount_0 = prev_2[waveId_0]?.count ?? 0;
          const currentLatestDropTimestamp_0 = prev_2[waveId_0]?.latestDropTimestamp ?? null;
          return {
            ...prev_2,
            [waveId_0]: {
              count: currentCount_0,
              latestDropTimestamp: Math.max(message.created_at, currentLatestDropTimestamp_0 ?? 0),
              firstUnreadSerialNo: prev_2[waveId_0]?.firstUnreadSerialNo ?? null
            }
          };
        });
      }
      setNewDropsCounts(prev_3 => {
        const currentCount_1 = prev_3[waveId_0]?.count ?? 0;
        const currentLatestDropTimestamp_1 = prev_3[waveId_0]?.latestDropTimestamp ?? null;
        const currentFirstUnread = prev_3[waveId_0]?.firstUnreadSerialNo ?? null;
        return {
          ...prev_3,
          [waveId_0]: {
            count: currentCount_1 + 1,
            latestDropTimestamp: Math.max(message.created_at, currentLatestDropTimestamp_1 ?? 0),
            firstUnreadSerialNo: currentFirstUnread === null ? message.serial_no : Math.min(currentFirstUnread, message.serial_no)
          }
        };
      });
    };
    $[15] = activeWaveId;
    $[16] = connectedProfile?.handle;
    $[17] = otherListWaveIds;
    $[18] = refetchWaves;
    $[19] = unknownWaveRefetchCooldownMs;
    $[20] = waves;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t11);
  let t12;
  if ($[22] !== newDropsCounts || $[23] !== resetAllWavesNewDropsCount || $[24] !== resetWaveNewDropsCount) {
    t12 = {
      newDropsCounts,
      resetWaveNewDropsCount,
      resetAllWavesNewDropsCount
    };
    $[22] = newDropsCounts;
    $[23] = resetAllWavesNewDropsCount;
    $[24] = resetWaveNewDropsCount;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}
export default useNewDropCounter;